import i18nFactory from '@yandex-int/i18n';

import { Shape2Icon, Shape2Tone } from '../../components/Shape2/Shape2';
import { BrandSurveyStatus, BrandSurveyStopReason, SurveyStatus } from '../../typings/campaigns';
import { IconBrandLift, TranslateBrandLift } from '../../typings/common';

import * as keyset from './brandlift.i18n';

const i18n = i18nFactory(keyset);

enum ClassProp {
    ACTIVE = 'active',
    DRAFT = 'draft',
    MODERATION = 'moderation',
    REJECT = 'reject',
    STOP = 'stop',
    COMPLETED = 'completed'
}

export const mapUnfeasibleReasonForText = (brandSurveyStopReason: BrandSurveyStopReason): string => {
    const mapper: Record<BrandSurveyStopReason, string> = {
        [BrandSurveyStopReason.LOW_BUDGET]: i18n('budget-grid'),
        [BrandSurveyStopReason.LOW_REACH]: i18n('coverage-grid')
    };

    if (mapper[brandSurveyStopReason]) {
        return mapper[brandSurveyStopReason];
    }

    throw new Error(`unfeasible reason ${brandSurveyStopReason} is not defined`);
};

export const getUnfeasibleReason = (brandSurveyStopReasons: BrandSurveyStopReason[]): string => {
    return brandSurveyStopReasons.map(mapUnfeasibleReasonForText).join(` ${i18n('and-grid')} `);
};

export const mapIconIframe = (surveyStatus: SurveyStatus): IconBrandLift => {
    const mapper: Record<SurveyStatus, IconBrandLift> = {
        [SurveyStatus.ACTIVE]: {
            icon: Shape2Icon.info,
            tone: Shape2Tone.blue
        },
        [SurveyStatus.COMPLETED]: {
            icon: Shape2Icon.check,
            tone: Shape2Tone.white
        },
        [SurveyStatus.MODERATION_REJECTED]: {
            icon: Shape2Icon.infoRed,
            tone: Shape2Tone.white
        },
        [SurveyStatus.DRAFT]: {
            icon: Shape2Icon.info,
            tone: Shape2Tone.dark
        },
        [SurveyStatus.MODERATION]: {
            icon: Shape2Icon.info,
            tone: Shape2Tone.yellow
        },
        [SurveyStatus.UNFEASIBLE]: {
            icon: Shape2Icon.infoRed,
            tone: Shape2Tone.white
        }
    };

    if (mapper[surveyStatus]) {
        return mapper[surveyStatus];
    }

    throw new Error(`Survey status ${surveyStatus} for icon is not defined`);
};

export const getPropsByStatus = (surveyStatus: SurveyStatus): ClassProp => {
    const mapper: Record<SurveyStatus, ClassProp> = {
        [SurveyStatus.ACTIVE]: ClassProp.ACTIVE,
        [SurveyStatus.COMPLETED]: ClassProp.COMPLETED,
        [SurveyStatus.MODERATION_REJECTED]: ClassProp.REJECT,
        [SurveyStatus.DRAFT]: ClassProp.DRAFT,
        [SurveyStatus.MODERATION]: ClassProp.MODERATION,
        [SurveyStatus.UNFEASIBLE]: ClassProp.STOP
    };

    if (mapper[surveyStatus]) {
        return mapper[surveyStatus];
    }

    throw new Error(`Survey status ${surveyStatus} for property is not defined`);
};

export const mapIconCell = (surveyStatus: SurveyStatus | null): IconBrandLift => {
    const mapper: Record<SurveyStatus, IconBrandLift> = {
        [SurveyStatus.ACTIVE]: {
            icon: Shape2Icon.time,
            tone: Shape2Tone.dark
        },
        [SurveyStatus.COMPLETED]: {
            icon: Shape2Icon.statistics,
            tone: Shape2Tone.dark
        },
        [SurveyStatus.MODERATION_REJECTED]: {
            icon: Shape2Icon.info,
            tone: Shape2Tone.red
        },
        [SurveyStatus.DRAFT]: {
            icon: Shape2Icon.draft,
            tone: Shape2Tone.dark
        },
        [SurveyStatus.MODERATION]: {
            icon: Shape2Icon.info,
            tone: Shape2Tone.dark
        },
        [SurveyStatus.UNFEASIBLE]: {
            icon: Shape2Icon.reject,
            tone: Shape2Tone.red
        }
    };

    if (surveyStatus && mapper[surveyStatus]) {
        return mapper[surveyStatus];
    }

    throw new Error(`Survey status ${surveyStatus} for icon is not defined`);
};

export const mapStatusCell = (brandSurveyStatus: BrandSurveyStatus): TranslateBrandLift => {
    const { surveyStatus, brandSurveyStopReasons } = brandSurveyStatus;

    const mapper: Record<SurveyStatus, TranslateBrandLift> = {
        [SurveyStatus.ACTIVE]: {
            title: i18n('active-grid'),
            text: i18n('show-params-grid')
        },
        [SurveyStatus.COMPLETED]: {
            title: i18n('completed-grid'),
            text: i18n('show-result-grid')
        },
        [SurveyStatus.MODERATION_REJECTED]: {
            title: i18n('reject-grid'),
            text: i18n('not-brand-grid')
        },
        [SurveyStatus.DRAFT]: {
            title: i18n('draft-grid'),
            text: i18n('show-params-grid')
        },
        [SurveyStatus.MODERATION]: {
            title: i18n('moderation-grid'),
            text: i18n('show-params-grid')
        },
        [SurveyStatus.UNFEASIBLE]: {
            title: i18n('stop-grid'),
            text: i18n('low-grid', {
                reason: getUnfeasibleReason(brandSurveyStopReasons)
            })
        }
    };

    if (surveyStatus && mapper[surveyStatus]) {
        return mapper[surveyStatus];
    }

    throw new Error(`Survey status ${surveyStatus} for text is not defined`);
};
